import {
  startOfMonth,
  endOfMonth,
  subMonths,
  parseISO,
  isWithinInterval,
} from 'date-fns';

/**
 * Rule-based finance chat engine.
 * Receives a question string + store data, returns a response string.
 */
export function processQuestion(
  question,
  { transactions, members, budgets, goals, formatCurrency, t }
) {
  const q = question.toLowerCase().trim();

  // Detect time scope
  const timeScope = detectTimeScope(q);

  // Detect member filter
  const memberMatch = detectMember(q, members);

  // Detect category filter
  const categoryMatch = detectCategory(q, t);

  // Filter transactions by time scope
  const filtered = filterByTime(transactions, timeScope);

  // --- Intent detection (order matters: most specific first) ---

  // Balance
  if (matches(q, ['balance', 'saldo'])) {
    return answerBalance(filtered, formatCurrency, t, timeScope);
  }

  // Budget status
  if (matches(q, ['presupuesto', 'budget'])) {
    return answerBudgets(budgets, formatCurrency, t);
  }

  // Goals status
  if (matches(q, ['meta', 'goal', 'ahorro', 'saving'])) {
    return answerGoals(goals, formatCurrency, t);
  }

  // Expenses by member
  if (
    memberMatch &&
    matches(q, ['gast', 'spent', 'expense', 'gasto'])
  ) {
    return answerExpensesByMember(
      filtered,
      memberMatch,
      formatCurrency,
      t,
      timeScope
    );
  }

  // Expenses by category
  if (
    categoryMatch &&
    matches(q, ['gast', 'spent', 'expense', 'gasto'])
  ) {
    return answerExpensesByCategory(
      filtered,
      categoryMatch,
      formatCurrency,
      t,
      timeScope
    );
  }

  // General expenses
  if (matches(q, ['gast', 'spent', 'expense', 'gasto'])) {
    return answerExpenses(filtered, formatCurrency, t, timeScope);
  }

  // Income
  if (matches(q, ['ingreso', 'income', 'salary', 'salario', 'ganado', 'earned'])) {
    return answerIncome(filtered, formatCurrency, t, timeScope);
  }

  // Fallback
  return {
    text: t('chat.notUnderstood'),
    suggestions: [
      t('chat.suggestion1'),
      t('chat.suggestion2'),
      t('chat.suggestion3'),
      t('chat.suggestion4'),
    ],
  };
}

// --- Helpers ---

function matches(q, keywords) {
  return keywords.some((kw) => q.includes(kw));
}

function detectTimeScope(q) {
  if (
    matches(q, [
      'mes pasado',
      'last month',
      'mes anterior',
      'previous month',
    ])
  ) {
    return 'lastMonth';
  }
  if (
    matches(q, ['este mes', 'this month', 'mes actual', 'current month'])
  ) {
    return 'thisMonth';
  }
  // Default to this month for most queries
  if (matches(q, ['hoy', 'today'])) {
    return 'thisMonth';
  }
  return 'all';
}

function detectMember(q, members) {
  if (!members || members.length === 0) return null;
  // Look for "en <name>" or "for <name>" or "de <name>" patterns
  for (const member of members) {
    const name = member.name.toLowerCase();
    if (
      q.includes(`en ${name}`) ||
      q.includes(`for ${name}`) ||
      q.includes(`de ${name}`) ||
      q.includes(`on ${name}`) ||
      q.includes(name)
    ) {
      return member;
    }
  }
  return null;
}

function detectCategory(q, t) {
  const categories = [
    'food',
    'transport',
    'housing',
    'entertainment',
    'health',
    'education',
    'shopping',
    'services',
    'salary',
    'freelance',
    'credit',
    'other',
  ];
  for (const cat of categories) {
    const translated = t(`categories.${cat}`).toLowerCase();
    if (q.includes(translated) || q.includes(cat)) {
      return cat;
    }
  }
  return null;
}

function filterByTime(transactions, scope) {
  const now = new Date();
  if (scope === 'thisMonth') {
    const start = startOfMonth(now);
    const end = endOfMonth(now);
    return transactions.filter((tx) =>
      isWithinInterval(parseISO(tx.date), { start, end })
    );
  }
  if (scope === 'lastMonth') {
    const lastMonth = subMonths(now, 1);
    const start = startOfMonth(lastMonth);
    const end = endOfMonth(lastMonth);
    return transactions.filter((tx) =>
      isWithinInterval(parseISO(tx.date), { start, end })
    );
  }
  return transactions;
}

function timeScopeLabel(timeScope, t) {
  if (timeScope === 'thisMonth') return t('chat.thisMonth');
  if (timeScope === 'lastMonth') return t('chat.lastMonth');
  return t('chat.allTime');
}

function answerBalance(filtered, formatCurrency, t, timeScope) {
  const income = filtered
    .filter((tx) => tx.type === 'income')
    .reduce((s, tx) => s + tx.amount, 0);
  const expenses = filtered
    .filter((tx) => tx.type === 'expense')
    .reduce((s, tx) => s + tx.amount, 0);
  const balance = income - expenses;
  const label = timeScopeLabel(timeScope, t);
  return {
    text: `${t('chat.balance')}: ${formatCurrency(balance)} (${label})\n${t('chat.totalIncome')}: ${formatCurrency(income)}\n${t('chat.totalExpenses')}: ${formatCurrency(expenses)}`,
  };
}

function answerExpenses(filtered, formatCurrency, t, timeScope) {
  const expenses = filtered.filter((tx) => tx.type === 'expense');
  const total = expenses.reduce((s, tx) => s + tx.amount, 0);
  const label = timeScopeLabel(timeScope, t);

  if (expenses.length === 0) {
    return { text: t('chat.noTransactions') };
  }

  // Top 3 categories
  const byCategory = {};
  expenses.forEach((tx) => {
    byCategory[tx.category] = (byCategory[tx.category] || 0) + tx.amount;
  });
  const top = Object.entries(byCategory)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([cat, amt]) => `  ${t(`categories.${cat}`)}: ${formatCurrency(amt)}`)
    .join('\n');

  return {
    text: `${t('chat.totalExpenses')} (${label}): ${formatCurrency(total)}\n\n${top}`,
  };
}

function answerIncome(filtered, formatCurrency, t, timeScope) {
  const incomes = filtered.filter((tx) => tx.type === 'income');
  const total = incomes.reduce((s, tx) => s + tx.amount, 0);
  const label = timeScopeLabel(timeScope, t);

  if (incomes.length === 0) {
    return { text: t('chat.noTransactions') };
  }

  const details = incomes
    .map((tx) => `  ${tx.description}: ${formatCurrency(tx.amount)}`)
    .join('\n');

  return {
    text: `${t('chat.totalIncome')} (${label}): ${formatCurrency(total)}\n\n${details}`,
  };
}

function answerExpensesByMember(filtered, member, formatCurrency, t, timeScope) {
  const expenses = filtered.filter(
    (tx) => tx.type === 'expense' && tx.memberIds?.includes(member.id)
  );
  const total = expenses.reduce((s, tx) => s + tx.amount, 0);
  const label = timeScopeLabel(timeScope, t);

  if (expenses.length === 0) {
    return {
      text: `${t('chat.memberExpenses', { name: member.name })}: ${formatCurrency(0)} (${label})`,
    };
  }

  return {
    text: `${t('chat.memberExpenses', { name: member.name })} (${label}): ${formatCurrency(total)} (${expenses.length} ${expenses.length === 1 ? 'mov.' : 'movs.'})`,
  };
}

function answerExpensesByCategory(filtered, category, formatCurrency, t, timeScope) {
  const expenses = filtered.filter(
    (tx) => tx.type === 'expense' && tx.category === category
  );
  const total = expenses.reduce((s, tx) => s + tx.amount, 0);
  const label = timeScopeLabel(timeScope, t);
  const catName = t(`categories.${category}`);

  return {
    text: `${t('chat.categoryTotal', { category: catName })} (${label}): ${formatCurrency(total)}`,
  };
}

function answerBudgets(budgets, formatCurrency, t) {
  if (!budgets || budgets.length === 0) {
    return { text: t('chat.noTransactions') };
  }

  const lines = budgets
    .map((b) => {
      const pct = b.limit > 0 ? Math.round((b.spent / b.limit) * 100) : 0;
      return `  ${t(`categories.${b.category}`)}: ${formatCurrency(b.spent)} / ${formatCurrency(b.limit)} (${pct}%)`;
    })
    .join('\n');

  return {
    text: `${t('chat.budgetStatus')}:\n\n${lines}`,
  };
}

function answerGoals(goals, formatCurrency, t) {
  if (!goals || goals.length === 0) {
    return { text: t('chat.noTransactions') };
  }

  const lines = goals
    .map((g) => {
      const pct = g.target > 0 ? Math.round((g.saved / g.target) * 100) : 0;
      return `  ${g.icon} ${g.name}: ${formatCurrency(g.saved)} / ${formatCurrency(g.target)} (${pct}%)`;
    })
    .join('\n');

  return {
    text: `${t('chat.goalStatus')}:\n\n${lines}`,
  };
}
